<template>
  <layout-public>
    <page-meta />

    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Blog'), href: route('blog.index') },
        { label: t('Blog Post') }
      ]"
      :title="post.title"
      :meta="metaText"
    />

    <section v-if="post.cover_image" class="blog-cover">
      <div class="container">
        <div class="row">
          <div class="col">
            <img :src="post.cover_image" :alt="post.title" data-aos="zoom-in" />
          </div>
        </div>
      </div>
    </section>

    <section class="blog-content-section">
      <div class="container">
        <div class="row">
          <div class="col">
            <div class="prose" v-html="safeContent" data-aos="fade-up"></div>
          </div>
        </div>
      </div>
    </section>

    <section class="blog-related-posts" v-if="relatedPosts.length">
      <div class="container">
        <div class="row">
          <div class="col">
            <section-title align="left" :title="t('Related posts')" data-aos="fade-up" />
            <!-- Three cards, and no `col-md-6` step. A three-card band split
                 two-up always strands the third on a row of its own, so below
                 992px these read one per row instead, and the band is never
                 half-full. -->
            <div class="row gy-4">
              <div v-for="related in relatedPosts" :key="related.slug" class="col-lg-4" data-aos="fade-up">
                <post-card :post="related" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.blog-cover {
  background: $ground-tint;
  padding-bottom: 0;
  padding-top: 2rem;

  img {
    border-radius: $r-sm;
    box-shadow: $shadow-card;
    display: block;
    height: auto;
    max-width: 100%;
    object-fit: contain;
    width: 100%;
  }
}

.blog-content-section {
  background: $ground-tint;
}

.blog-related-posts {
  background: $ground-tint;
}

</style>

<script>
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import PageMeta from '@/Components/PageMeta.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import PageHero from '@/Components/PageHero.vue';
import PostCard from '@/Components/PostCard.vue';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    LayoutPublic,
    PageHero,
    PageMeta,
    PostCard,
    SectionTitle
  },

  props: {
    post: {
      type: Object,
      required: true
    },
    relatedPosts: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    safeContent() {
      return DOMPurify.sanitize(this.post.content);
    },

    /* The date and author line that sits under the hero title, the same meta
       line Automation/Preview renders. Each part is added only when the post has
       it, so a post with no author cannot print "By" against nothing and one
       with no date cannot print an invalid one. The separator is a real
       character rather than an HTML entity, because the hero renders the prop as
       text and `&middot;` would arrive on the page as literal characters. */
    metaText() {
      const parts = [];

      if (this.post.created_at) parts.push(this.formatDate(this.post.created_at));
      if (this.post.author) parts.push(this.t('By') + ' ' + this.post.author);

      return parts.join(' · ');
    }
  },

  methods: {
    formatDate(isoString) {
      return new Date(isoString).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Blog Post': 'Blog Post',
          'Related posts': 'Related posts'
        },
        'zh-MY': {
          'Blog Post': '博客文章',
          'Related posts': '相关文章'
        },
        'ms-MY': {
          'Blog Post': 'Artikel Blog',
          'Related posts': 'Artikel Berkaitan'
        }
      }
    });

    return { t };
  }
};
</script>
